import { useState, useEffect } from 'preact/hooks';
import { trackCaptureEvent } from '../lib/captureAnalytics';
import { navigate } from '../lib/navigate';
import { guestApi, publicApi, setCsrfToken } from '../api/client';
import type { InvitationPreview } from '../api/types';
import { useAuth } from '../hooks/useAuth';
import { DisplayNameForm } from '../components/DisplayNameForm';
import { Skeleton } from '../components/Skeleton';
import {
  ChorusHeader,
  ChorusEmpty,
  ChorusHero,
  inviteIllustrationLabels,
  AvatarStack,
  authorColor,
  IconArrow,
  IconPlus,
} from '../components/chorus';
import styles from './JoinPage.module.css';

const THREAD_TYPE_LABELS: Record<string, string> = {
  memory: 'Throwback',
  legacy: 'Origin Story',
  event: 'Event',
  topic: 'Topic',
  milestone: 'Milestone',
};

const THREAD_TYPE_DESCRIPTIONS: Record<string, string> = {
  memory: 'A shared memory told from multiple perspectives.',
  legacy: 'A life story told through many voices.',
  event: 'A shared experience captured from different angles.',
  topic: 'An open discussion from different viewpoints.',
  milestone: 'A milestone celebrated by the people who were there.',
};

interface JoinPageProps {
  token?: string;
  path?: string;
  isPhaseInvite?: boolean;
}

export function JoinPage({ token, isPhaseInvite }: JoinPageProps) {
  const [step, setStep] = useState<'loading' | 'name' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  // The plot premise is drafted asynchronously right after a thread is created,
  // so a fresh invite can land before plotSummary exists. While we poll for it,
  // show a pending state instead of silently hiding the section.
  const [plotPending, setPlotPending] = useState(false);
  const { setGuestSession } = useAuth();

  useEffect(() => {
    if (!token) {
      setStep('error');
      setError('No invite link found. Check that the full link was copied.');
      return;
    }

    // Top of the guest acquisition funnel. Everything downstream is measured
    // against this denominator.
    trackCaptureEvent('join_link_opened', {
      surface: 'join',
      entry: isPhaseInvite ? 'phase_invite' : 'space_invite',
    });

    const fetchPreview = isPhaseInvite
      ? publicApi.getPhaseInvitePreview(token)
      : publicApi.getInvitationPreview(token);

    fetchPreview
      .then((data) => {
        setPreview(data);
        setStep('name');
      })
      .catch((err) => {
        const code = err?.code;
        if (code === 'INVITATION_EXPIRED') {
          setError("This invite has expired. Ask whoever invited you for a fresh link — they can send a new one in seconds.");
          setErrorCode(code);
          setStep('error');
        } else if (code === 'INVITATION_CONSUMED') {
          setError("This invite has already been used up. Ask whoever invited you for a fresh link — they can send a new one in seconds.");
          setErrorCode(code);
          setStep('error');
        } else if (code === 'INVALID_INVITATION') {
          setError("This invite link isn't working. It may have been revoked or the moment was deleted.");
          setErrorCode(code);
          setStep('error');
        } else {
          setStep('name');
        }
      });
  }, [token, isPhaseInvite]);

  // Bounded poll for a late-arriving plot premise. Guests have no socket room,
  // so we re-fetch the preview a few times with exponential backoff and stop as
  // soon as plotSummary lands (or the attempt cap is hit — the moment may simply
  // have no plot). Cancels on unmount so we never hammer the endpoint.
  useEffect(() => {
    if (step !== 'name' || !token || !preview || preview.plotSummary) {
      setPlotPending(false);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    const MAX_ATTEMPTS = 4;

    setPlotPending(true);

    const refetch = () =>
      isPhaseInvite
        ? publicApi.getPhaseInvitePreview(token)
        : publicApi.getInvitationPreview(token);

    const schedule = () => {
      // Exponential backoff, capped: 2s, 4s, 8s, 16s.
      const delay = Math.min(2000 * 2 ** attempt, 16000);
      timer = setTimeout(async () => {
        if (cancelled) return;
        attempt += 1;
        try {
          const fresh = await refetch();
          if (cancelled) return;
          if (fresh.plotSummary) {
            setPreview(fresh);
            setPlotPending(false);
            return;
          }
        } catch {
          // Swallow transient errors; the attempt cap still bounds the loop.
        }
        if (cancelled) return;
        if (attempt >= MAX_ATTEMPTS) {
          setPlotPending(false);
          return;
        }
        schedule();
      }, delay);
    };

    schedule();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [step, token, isPhaseInvite, preview]);

  const handleNameSubmit = async (displayName: string) => {
    if (!token) return;

    try {
      const session = isPhaseInvite
        ? await guestApi.createSessionFromPhaseInvite(token, displayName)
        : await guestApi.createSession(token, displayName);
      trackCaptureEvent('session_created', {
        surface: 'join',
        entry: isPhaseInvite ? 'phase_invite' : 'space_invite',
        spaceId: session.spaceId,
        threadId: session.threadId ?? undefined,
      });
      if (session.csrfToken) {
        setCsrfToken(session.csrfToken);
      }

      setGuestSession({
        sessionToken: session.sessionToken,
        spaceId: session.spaceId,
        spaceName: session.spaceName,
        threadId: session.threadId ?? null,
        // Carry the server's scope through — the "My Stories" store keys its
        // dedupe on it, so dropping it makes two thread invites in one crew
        // clobber each other (W7).
        accessScope: session.accessScope,
        expiresAt: session.expiresAt,
        sessionId: session.sessionId,
        displayName,
      });

      if (session.threadId) {
        const query = session.phaseIndex != null ? `?phase=${session.phaseIndex}` : '';
        navigate(`/contribute/${session.threadId}${query}`);
      } else {
        setError("No moments here yet. The crew owner hasn't started one. Check back soon!");
        setStep('error');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't join this crew. Please try again.");
      setStep('error');
    }
  };

  if (step === 'loading') {
    return (
      <div class={styles.page}>
        <ChorusHeader animate={false} />
        <div class={styles.skeletonWrap}>
          <Skeleton width="60%" height="14px" />
          <div style={{ height: '14px' }} />
          <div class={styles.skeletonCard}>
            <Skeleton width="100%" height="0" borderRadius="var(--st-radius-card, 18px)" />
            <div class={styles.skeletonHero} aria-hidden="true">
              <Skeleton width="100%" height="100%" borderRadius="var(--st-radius-card, 18px)" />
            </div>
            <div style={{ height: '14px' }} />
            <Skeleton width="40%" height="12px" />
            <div style={{ height: '12px' }} />
            <Skeleton width="85%" height="24px" />
            <div style={{ height: '12px' }} />
            <Skeleton width="55%" height="14px" />
            <div style={{ height: '14px' }} />
            <Skeleton width="100%" height="80px" borderRadius="var(--st-radius-md)" />
          </div>
          <div class={styles.skeletonAfter}>
            <Skeleton width="55%" height="14px" />
            <div style={{ height: '14px' }} />
            <Skeleton width="100%" height="52px" borderRadius="var(--st-radius-md)" />
            <div style={{ height: '12px' }} />
            <Skeleton width="100%" height="52px" borderRadius="var(--st-radius-md)" />
          </div>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div class={styles.page}>
        <ChorusHeader animate={false} />
        <div class={styles.errorContainer}>
          <div class={styles.errorEmpty}>
            <ChorusEmpty size={120} variant="dispersed" />
          </div>
          <h2 class={styles.errorTitle}>
            {errorCode === 'INVITATION_EXPIRED'
              ? 'Invite Expired'
              : errorCode === 'INVITATION_CONSUMED'
                ? 'Invite Used Up'
                : 'Unable to Join'}
          </h2>
          <p class={styles.errorMessage}>{error || 'Something went wrong'}</p>
          <a href="/" class={styles.errorLink}>
            Go to OurTake home <IconArrow />
          </a>
        </div>
      </div>
    );
  }

  const threadType = preview?.threadType ?? null;
  const typeLabel = threadType ? THREAD_TYPE_LABELS[threadType] ?? threadType : null;
  const typeDesc = threadType ? THREAD_TYPE_DESCRIPTIONS[threadType] : null;

  return (
    <div class={styles.page}>
      <ChorusHeader />

      <div class={styles.intro}>
        {(preview?.senderDisplayName || preview?.creatorName) && (
          <p class={styles.inviteText}>
            <span class={styles.inviteName}>{preview.senderDisplayName || preview.creatorName}</span>
            {isPhaseInvite
              ? preview.threadTitle
                ? <> invited you to share your take on <em>{preview.threadTitle}</em>.</>
                : ' invited you to share your take.'
              : ' invited you to join.'}
          </p>
        )}

        {preview?.greetingAudioUrl && (
          <div class={styles.greetingCard}>
            <div class={styles.greetingLabel}>
              A hello from {preview.creatorName ?? 'your crew'}
            </div>
            <audio
              controls
              preload="metadata"
              src={preview.greetingAudioUrl}
              class={styles.greetingAudio}
              aria-label={`Greeting from ${preview.creatorName ?? 'the crew'}`}
            />
          </div>
        )}

        {preview && (
          <div class={styles.previewCard}>
            <div class={styles.heroRegion}>
              {preview.coverImageUrl ? (
                <img
                  src={preview.coverImageUrl}
                  alt={
                    preview.threadTitle
                      ? `Cover image for ${preview.threadTitle}`
                      : `Cover image for ${preview.spaceName}`
                  }
                  class={styles.heroImage}
                  loading="eager"
                />
              ) : (
                <div class={styles.heroFallback} aria-hidden="true">
                  <ChorusHero compact animate={false} labels={inviteIllustrationLabels(preview.invitationIllustrationNames)} />
                </div>
              )}
            </div>

            {preview.templateDisplayName && (
              <div class={styles.eyebrow}>{preview.templateDisplayName}</div>
            )}

            {preview.threadTitle && (
              <div class={styles.threadTitle}>{preview.threadTitle}</div>
            )}

            {preview.phasePrompt && (
              <div class={styles.promptPullquote} role="note">
                {preview.phasePrompt}
              </div>
            )}

            <div class={styles.previewMeta}>
              <AvatarStack
                size={22}
                overlap={7}
                colors={[
                  authorColor(preview.spaceName + 'a'),
                  authorColor(preview.spaceName + 'b'),
                  authorColor(preview.spaceName + 'c'),
                ]}
              />
              <span>
                {preview.spaceName} · {preview.memberCount} {preview.memberCount === 1 ? 'member' : 'members'}
                {preview.threadCount > 0 && (
                  <> · {preview.threadCount} {preview.threadCount === 1 ? 'moment' : 'moments'}</>
                )}
              </span>
            </div>

            {preview.threadTitle && (
              <div class={styles.threadMeta}>
                {typeLabel && <span class={styles.typeBadge}>{typeLabel}</span>}
                {preview.creatorName && (
                  <span class={styles.threadStarted}>Started by {preview.creatorName}</span>
                )}
              </div>
            )}

            {preview.threadDescription && (
              <p class={styles.threadDescription}>{preview.threadDescription}</p>
            )}

            {(preview.plotSummary || plotPending) && (
              <div class={styles.plotSection}>
                <div class={styles.plotHeader}>The Plot</div>
                {preview.plotSummary ? (
                  <p class={styles.plotText}>{preview.plotSummary}</p>
                ) : (
                  <div class={styles.plotPending} role="status" aria-live="polite">
                    <Skeleton width="100%" height="13px" />
                    <div style={{ height: '7px' }} />
                    <Skeleton width="82%" height="13px" />
                    <p class={styles.plotPendingHint}>Still crafting your story…</p>
                  </div>
                )}
              </div>
            )}

            {typeDesc && typeLabel && (
              <div class={styles.typeExplanation}>
                <span class={styles.typeExplanationStrong}>{typeLabel.toLowerCase()}</span> — {typeDesc}
              </div>
            )}

            {preview.phaseName && (
              <div class={styles.phaseInfo}>
                <span class={styles.phaseArrow}>▸</span>
                Phase active: {preview.phaseName}
              </div>
            )}
          </div>
        )}
      </div>

      <div class={styles.aboutWrap}>
        <button
          type="button"
          class={styles.aboutToggle}
          onClick={() => setAboutExpanded(!aboutExpanded)}
          aria-expanded={aboutExpanded}
          aria-controls="join-about-content"
        >
          <span class={styles.aboutToggleLeft}>What is OurTake?</span>
          <span
            class={`${styles.aboutToggleSym} ${aboutExpanded ? styles.aboutToggleSymOpen : ''}`}
            aria-hidden="true"
          >
            <IconPlus size={14} />
          </span>
        </button>
        <div id="join-about-content" class={styles.aboutContent} hidden={!aboutExpanded}>
          <p>
            When you share an experience, everyone sees it differently. OurTake is the only place
            where all those versions come together into the full story — not just yours.
          </p>
          <p>Your voice joins the others. There are no wrong answers.</p>
        </div>
      </div>

      <div class={styles.nameWrap}>
        <h2 class={styles.nameTitle}>What should we call you?</h2>
        <p class={styles.nameHint}>
          This is how others will see you in the moment. First name is fine.
        </p>
        <DisplayNameForm onSubmit={handleNameSubmit} />
      </div>
    </div>
  );
}
